import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiResponse,
  UpdateProfilePayload,
  UserBankAccount,
  UserProfile,
} from '../models/auth.models';
import { ApiConfigService } from './api-config.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService
  ) {}

  getProfile(username: string, rememberProfile = false): Observable<UserProfile | null> {
    const url =
      this.apiConfig.buildUrl('DESIGN', 'USER', 'GET_PROFILE') +
      `${username}/${rememberProfile}`;

    return this.http.get<ApiResponse<Record<string, unknown>>>(url).pipe(
      map((response) => this.mapProfile(response.data)),
    );
  }

  /** PUT /api-design{suffixe}/user/v1/update-profile */
  updateProfile(payload: UpdateProfilePayload): Observable<ApiResponse<unknown>> {
    const url = this.apiConfig.buildUrl('DESIGN', 'USER', 'UPDATE_PROFILE');
    return this.http.put<ApiResponse<unknown>>(url, payload);
  }

  private mapProfile(raw: Record<string, unknown> | null | undefined): UserProfile | null {
    if (!raw) {
      return null;
    }

    return {
      id: this.asString(raw['id']),
      name: this.asString(raw['name'] ?? raw['lastName']),
      fName: this.asString(raw['firstName'] ?? raw['pname'] ?? raw['fName']),
      lName: this.asString(raw['lastName'] ?? raw['name'] ?? raw['lName']),
      email: this.asString(raw['email']),
      phone: this.asString(raw['username'] ?? raw['phone']),
      codePays: this.asString(raw['codepays'] ?? raw['codePays'] ?? raw['prefixCountry']),
      address: this.asString(raw['address']),
      codeClient: this.asString(raw['code'] ?? raw['code_assure'] ?? raw['codeClient'] ?? raw['codeAssure']),
      niu: this.asString(raw['niu']),
      dateNaiss: this.asString(raw['dateNaiss'] ?? raw['dateNaissance']),
      photoProfil: this.asString(raw['photoProfil']),
      isActivated: this.parseIsActivated(raw['isActivated']),
      bankAccounts: this.mapBankAccounts(raw['bankAccounts']),
    };
  }

  private mapBankAccounts(raw: unknown): UserBankAccount[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item) => {
        const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        return {
          id: this.asString(record['id']),
          bankName: this.asString(record['bankName']),
          bankCode: this.asString(record['bankCode']),
          accountNumber: this.asString(record['accountNumber']),
        };
      })
      .filter((account) => account.bankCode || account.bankName || account.accountNumber);
  }

  private parseIsActivated(value: unknown): boolean {
    if (value === true || value === 1) {
      return true;
    }
    const text = String(value ?? '').trim().toUpperCase();
    return text === 'TRUE' || text === 'O' || text === '1';
  }

  private asString(value: unknown): string {
    if (value == null) {
      return '';
    }
    const text = String(value).trim();
    return text === 'null' || text === 'undefined' ? '' : text;
  }
}
